<template>
  <div>
    <section v-if="clientInfo">
      <v-card class="mb-8 pa-6" elevation="2">
        <v-card-title class="d-flex flex-wrap pa-0">
          <div class="flex-grow-1">
            <h4 v-if="$vuetify.breakpoint.mdAndUp" class="text-h4 font-weight-semibold">
              {{isNew ? 'Create Client' : 'Edit Client: '+ clientInfo.id + ' - ' + clientInfo.name}}
            </h4>
            <div v-else class="mobile-title">
              <h4 class="text-h4 font-weight-semibold">{{isNew ? 'Create Client' : 'Edit Client: ' + clientInfo.id}}</h4>
              <div v-if="!isNew" class="text-subtitle-1 mt-1">{{clientInfo.name}}</div>
            </div>
          </div>

          <div class="d-flex align-center" style="gap: 12px;">
            <!-- Desktop: text + icon buttons -->
            <template v-if="$vuetify.breakpoint.mdAndUp">
              <v-btn
                color="primary"
                class="action-btn mr-3"
                @click="toggleBarcodeDialog"
                height="40">
                <v-icon left>mdi-barcode</v-icon>
                Barcode Identifiers
              </v-btn>
              <v-btn
                v-if="readPricingPermission"
                color="primary"
                class="action-btn mr-3"
                @click="getPricing"
                height="40">
                <v-icon left>local_atm</v-icon>
                Client Pricing
              </v-btn>
              <v-btn
                v-if="editPermissionsPermission"
                color="primary"
                class="action-btn"
                @click="editPermissions"
                height="40">
                <v-icon left>lock_open</v-icon>
                Default Permissions
              </v-btn>
            </template>

            <!-- Mobile: icon-only buttons with tooltips -->
            <template v-else>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    color="primary"
                    v-on="on"
                    @click="toggleBarcodeDialog"
                    class="mobile-action-btn mr-3">
                    <v-icon>mdi-barcode</v-icon>
                  </v-btn>
                </template>
                <span>Edit client Barcode Identifiers</span>
              </v-tooltip>
              <v-tooltip bottom v-if="readPricingPermission">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    color="primary"
                    v-on="on"
                    @click="getPricing"
                    class="mobile-action-btn mr-3">
                    <v-icon>local_atm</v-icon>
                  </v-btn>
                </template>
                <span>Edit client pricing</span>
              </v-tooltip>
              <v-tooltip bottom v-if="editPermissionsPermission">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    color="primary"
                    v-on="on"
                    @click="editPermissions"
                    class="mobile-action-btn">
                    <v-icon>lock_open</v-icon>
                  </v-btn>
                </template>
                <span>Edit default permissions</span>
              </v-tooltip>
            </template>
          </div>
        </v-card-title>
      </v-card>

      <v-container fluid class="edit-client-grid">
          <v-row justify="space-around">
            <v-col cols="12" sm="12" md="6">

              <v-expansion-panels accordion multiple v-model="leftPanels">

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>Client Information</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <ClientInformation
                      v-if="clientInfo"
                      :client-info="clientInfo"
                      :readonly="!isNew"
                      @focus-oracle-site-number="focusOracleSiteNumber">
                    </ClientInformation>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>Contact Details</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <ClientDetails
                      v-if="clientInfo"
                      ref="clientDetails"
                      :other-fields="true"
                      :client-info="clientInfo">
                    </ClientDetails>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>Accounting Information</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <AccountingInformation
                      v-if="clientInfo"
                      ref="accountingInformation"
                      :client-info="clientInfo">
                    </AccountingInformation>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>SMS Settings</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <SmsSettings
                      v-if="clientInfo"
                      :client-info="clientInfo">
                    </SmsSettings>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>Report Settings</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <ReportSettings
                      v-if="clientInfo"
                      :client-info="clientInfo">
                    </ReportSettings>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>LineHaul Settings</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <ClientLinehaulSetting
                      v-if="clientInfo"
                      ref="clientLinehaulSetting"
                      :client-info="clientInfo">
                    </ClientLinehaulSetting>
                  </v-expansion-panel-content>
                </v-expansion-panel>

              </v-expansion-panels>

            </v-col>

            <v-col cols="12" sm="12" md="6">

              <v-expansion-panels accordion multiple>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>POD Settings</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <PodSettings
                      v-if="clientInfo"
                      :client-info="clientInfo">
                    </PodSettings>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>Tracking Settings</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <TrackingSettings
                      v-if="clientInfo"
                      :client-info="clientInfo">
                    </TrackingSettings>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>Production Information</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <ProductionInformation
                      v-if="clientInfo"
                      :client-info="clientInfo">
                    </ProductionInformation>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>Courier Settings</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <CourierSettings
                      v-if="clientInfo"
                      :client-info="clientInfo">
                    </CourierSettings>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>API Settings</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <ApiSettings
                      :client-id="id">
                    </ApiSettings>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h2>Client Services</h2>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <ClientServices
                      v-if="clientInfo"
                      ref="clientServices"
                      :client-info="clientInfo">
                    </ClientServices>
                  </v-expansion-panel-content>
                </v-expansion-panel>

              </v-expansion-panels>

            </v-col>
          </v-row>
          <v-card-actions class="justify-center pt-8">
            <v-btn
              outlined
              color="grey darken-1"
              height="44"
              class="mr-4"
              :to="'/clients'">
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              height="44"
              @click="saveChanges()">
              Save
            </v-btn>
          </v-card-actions>
        </v-container>
    </section>

    <v-dialog width="800px" v-model="pricingShow" v-if="clientPricing && readPricingPermission">
      <ClientPricing :toggle-dialog="toggleDialog"
                     :client-pricing="clientPricing"
                     :id="id"
      ></ClientPricing>
    </v-dialog>
    <v-dialog width="800px" v-model="permissionsShow" v-if="clientPermissions && editPermissionsPermission">
      <ClientPermissions :toggle-dialog="togglePermissionsDialog"
                         :client-permissions="clientPermissions"
                         :id="id"
      ></ClientPermissions>
    </v-dialog>
    <v-dialog width="800px" v-model="barcodeShow" v-if="barcodeShow">
      <ClientBarcodes :toggle-dialog="toggleBarcodeDialog"
                     :client-barcode="barcodeIdentifiers"
                      v-on:update:identifiers="barcodeIdentifiers = $event"
                     :id="id"
      ></ClientBarcodes>
    </v-dialog>
    <ValidationErrorDialog />

    <v-overlay :value="saving" z-index="9999" class="saving-overlay">
      <div class="saving-content">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
          width="4"
        ></v-progress-circular>
        <div class="mt-4 saving-text">Saving client...</div>
      </div>
    </v-overlay>

    <v-dialog v-model="createSuccessDialog" max-width="500px" persistent>
      <v-card class="success-dialog" elevation="8">
        <v-card-title class="justify-center dialog-title">
          <v-icon color="success" size="48" class="mr-2">check_circle</v-icon>
          <span class="text-h5 font-weight-semibold">Client Created</span>
        </v-card-title>
        <v-card-text class="text-center dialog-content">
          <div class="mb-2"><strong>{{ clientInfo.name }}</strong></div>
          <div>The client was created successfully.</div>
        </v-card-text>
        <v-card-actions class="justify-center dialog-actions">
          <v-btn
            outlined
            color="grey darken-1"
            height="44"
            class="action-btn"
            @click="goToClientsPage">
            Go to clients page
          </v-btn>
          <v-btn
            color="primary"
            height="44"
            class="action-btn"
            @click="goToEditCreatedClient">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
    import axios from 'axios'
    import ClientInformation from "./util/Clients/ClientInformation";
    import ClientDetails from "./util/Clients/ClientDetails";
    import AccountingInformation from "./util/Clients/AccountingInformation"
    import SmsSettings from "./util/Clients/SmsSettings";
    import ReportSettings from "./util/Clients/ReportSettings";
    import PodSettings from "./util/Clients/PodSettings";
    import TrackingSettings from "./util/Clients/TrackingSettings";
    import ProductionInformation from "./util/Clients/ProductionInformation";
    import CourierSettings from "./util/Clients/CourierSettings";
    import ClientBarcodes from "./util/Clients/ClientBarcodes";
    import ClientPricing from "./util/Clients/ClientPricing";
    import ClientPermissions from "./util/Clients/ClientPermissions";
    import ApiSettings from "./util/Clients/ApiSettings";
    import ClientLinehaulSetting from "./util/Clients/ClientLinehaulSetting.vue";
    import ClientServices from "./util/Clients/ClientServices.vue";
    import ValidationErrorDialog from "./util/ValidationErrorDialog";

    export default {
        name: "EditClient",
        components: {
            ClientLinehaulSetting,
            ClientBarcodes,
            ClientPricing,
            ClientPermissions,
            CourierSettings,
            ProductionInformation,
            TrackingSettings,
            PodSettings,
            ReportSettings,
            SmsSettings,
            AccountingInformation,
            ClientDetails,
            ClientInformation,
            ApiSettings,
            ClientServices,
            ValidationErrorDialog
        },
        data() {
            return {
                id: null,
                barcodeIdentifiers: null,
                barcodeShow: false,
                clientInfo: null,
                clientPricing: null,
                clientPermissions: null,
                pricingShow: false,
                permissionsShow: false,
                barcode:[],
                isBarcode:true,
                createSuccessDialog: false,
                createdClientId: null,
                leftPanels: [],
                saving: false,
            }
        },
        methods: {
            getClientInfo(id) {
                axios.get(process.env.API_URL + '/clients/' + id)
                    .then((response) => {
                        this.clientInfo = response.data;
                    })
                    .catch((error) => {
                        console.log(error.response ? error.response.data : error);
                    })
                axios.get(process.env.API_URL + '/clients/' + id + '/barcode')
                  .then((response) => {
                    this.barcodeIdentifiers = response.data.map(item => item.identifier);
                  })
                  .catch((err) => {
                    this.clientInfo = null; // Avoid accidentally deleting barcode identifiers if not loaded correctly
                    this.handleError(err);
                  });
            },
            saveChanges() {
                // Clear any previous validation errors
                this.$store.commit('clearValidationErrors');

                // Client-side validation
                const errors = this.validateClientInfo();
                if (Object.keys(errors).length > 0) {
                    this.$store.commit('displayValidationDialog', {
                        message: 'Please correct the following errors before saving:',
                        errors: errors
                    });
                    return;
                }

                if (this.barcodeIdentifiers) {
                  var barcodeUpcase = [];
                  this.barcodeIdentifiers.forEach((item) => {
                    barcodeUpcase.push(item.toUpperCase());
                  });
                  this.clientInfo.barcode_identifier = barcodeUpcase;
                }

                // Show loading overlay
                this.saving = true;

                if (!this.isNew) {
                    axios.put(process.env.API_URL + '/clients/' + this.id, this.clientInfo)
                        .then((response) => {
                            this.clientInfo = response.data;
                            this.$router.push('/clients');
                        })
                        .catch((err) => {
                            this.handleError(err);
                        })
                        .finally(() => {
                            this.saving = false;
                        })
                } else {
                    // Collect pending contacts and restricted states for new client
                    const pendingContacts = this.$refs.clientDetails ? this.$refs.clientDetails.getPendingContacts() : [];
                    const pendingRestrictedStates = this.$refs.clientLinehaulSetting ? this.$refs.clientLinehaulSetting.getPendingRestrictedStates() : [];
                    const pendingClientServices = this.$refs.clientServices ? this.$refs.clientServices.getPendingClientServices() : [];

                    // Add to clientInfo for backend processing
                    if (pendingContacts.length > 0) {
                        this.clientInfo.contacts = pendingContacts.map(contact => ({
                            name: contact.name,
                            email: contact.email,
                            phone: contact.phone,
                            ops: contact.ops,
                            cc: contact.cc,
                            it: contact.it,
                            billing: contact.billing
                        }));
                    }

                    if (pendingRestrictedStates.length > 0) {
                        this.clientInfo.restricted_states = pendingRestrictedStates.map(state => state.state);
                    }

                    if (pendingClientServices.length > 0) {
                        this.clientInfo.client_services = pendingClientServices.map(service => service.id);
                    }

                    // Buffered pricing rows (non-postcode-billing clients only)
                    if (this.clientPricing && this.clientPricing.length > 0) {
                        this.clientInfo.pricing = this.clientPricing.map(pricing => ({
                            region_id: parseInt(pricing.region_id),
                            category: pricing.category,
                            base_rate: parseFloat(pricing.base_rate),
                            unit_rate: parseFloat(pricing.unit_rate),
                            unit_rate_after: parseInt(pricing.unit_rate_after)
                        }));
                    }

                    // Buffered default permissions (region defaults to Global/0)
                    if (this.clientPermissions && this.clientPermissions.length > 0) {
                        this.clientInfo.permissions = this.clientPermissions.map(permission => ({
                            permission_id: permission.id,
                            region_id: 0
                        }));
                    }

                    axios.post(process.env.API_URL + '/clients', this.clientInfo)
                        .then((response) => {
                            this.clientInfo = response.data;
                            this.createdClientId = response.data.id;
                            if (response.data.warnings && response.data.warnings.length > 0) {
                                this.$store.commit('displaySnackbar', response.data.warnings.join(' '));
                            }
                            this.createSuccessDialog = true;
                        })
                        .catch((err) => {
                            this.handleError(err);
                        })
                        .finally(() => {
                            this.saving = false;
                        })
                }
            },
            validateClientInfo() {
                const errors = {};

                // Required field validations
                if (!this.clientInfo.name || this.clientInfo.name.trim() === '') {
                    errors.name = 'Name is required';
                }

                if (!this.clientInfo.default_country) {
                    errors.default_country = 'Country is required';
                }

                // Email format validation for google_domain if provided
                if (this.clientInfo.google_domain && this.clientInfo.google_domain.trim() !== '') {
                    const emailDomainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z]{2,})+$/;
                    if (!emailDomainRegex.test(this.clientInfo.google_domain)) {
                        errors.google_domain = 'Invalid domain format (e.g., example.com)';
                    }
                }

                // Phone validation if provided
                if (this.clientInfo.phone && this.clientInfo.phone.trim() !== '') {
                    const phoneRegex = /^(\+\d{2}[ \-]{0,1}){0,1}(((\({0,1}[ \-]{0,1})0{0,1}\){0,1}[2|3|7|8]{1}\){0,1}[ \-]*(\d{4}[ \-]{0,1}\d{4}))|(1[ \-]{0,1}(300|800|900|902)[ \-]{0,1}((\d{6})|(\d{3}[ \-]{0,1}\d{3})))|(13[ \-]{0,1}([\d \-]{5})|((\({0,1}[ \-]{0,1})0{0,1}\){0,1}4{1}[\d \-]{8,10})))$/;
                    if (!phoneRegex.test(this.clientInfo.phone)) {
                        errors.phone = 'Invalid phone number format';
                    }
                }

                return errors;
            },
            goToEditCreatedClient() {
                const id = this.createdClientId;
                this.createSuccessDialog = false;
                this.$router.push('/edit-client?id=' + id);
            },
            goToClientsPage() {
                this.createSuccessDialog = false;
                this.$router.push('/clients');
            },
            handleError(err) {
                if (err.response && err.response.data) {
                    const data = err.response.data;
                    if (data.validation_errors) {
                        // Show validation dialog with structured errors
                        this.$store.commit('displayValidationDialog', {
                            message: data.error || 'Validation failed',
                            errors: data.validation_errors
                        });
                    } else if (data.error) {
                        // Show the specific error message in the validation dialog
                        this.$store.commit('displayValidationDialog', {
                            message: data.error,
                            errors: {}
                        });
                    } else {
                        // Log full error for debugging and show technical details
                        console.error('API Error:', err.response);
                        this.$store.commit('displayValidationDialog', {
                            message: 'Server returned ' + err.response.status + ' - ' +
                                (err.response.statusText || 'Request failed'),
                            errors: {}
                        });
                    }
                } else if (err.request) {
                    // Request made but no response received
                    console.error('Network Error:', err.request);
                    this.$store.commit('displaySnackbar',
                        'ERROR: Unable to reach server. Please check your connection.');
                } else {
                    // Something else went wrong
                    console.error('Error:', err.message);
                    this.$store.commit('displaySnackbar', 'ERROR: ' + err.message);
                }
            },
            getPricing() {
                if (this.clientInfo && this.clientInfo.use_postcode_billing) {
                    alert('This client is currently using postcode billing, please contact support to change pricing data');
                } else if (this.id) {
                    axios.get(process.env.API_URL + '/clients/' + this.id + '/pricing')
                        .then((response) => {
                            this.clientPricing = response.data;
                            this.pricingShow = true;
                        })
                        .catch((err) => {
                            this.handleError(err);
                        });
                } else {
                    // New (unsaved) client: keep any rows already buffered so
                    // re-opening the dialog does not discard the user's changes.
                    if (!this.clientPricing) {
                        this.clientPricing = [];
                    }
                    this.pricingShow = true;
                }
            },
            editPermissions() {
                if (this.id) {
                    axios.get(process.env.API_URL + '/clients/' + this.id + '/regions/0/permissions')
                        .then((response) => {
                            if (response.status === 200) {
                                this.clientPermissions = response.data;
                            } else {
                                this.clientPermissions = [];
                            }
                            this.permissionsShow = true;
                        })
                        .catch((err) => {
                            this.handleError(err);
                        });
                } else {
                    // New (unsaved) client: keep any permissions already ticked so
                    // re-opening the dialog does not discard the user's changes.
                    if (!this.clientPermissions) {
                        this.clientPermissions = [];
                    }
                    this.permissionsShow = true;
                }
            },
            toggleDialog() {
                this.pricingShow = !this.pricingShow;
            },
            togglePermissionsDialog() {
                this.permissionsShow = !this.permissionsShow;
            },
            toggleBarcodeDialog() {
              this.barcodeShow = !this.barcodeShow;
            },
            focusOracleSiteNumber(attempt = 0) {
                // Accounting Information is the 3rd panel (index 2) in the left column
                const accountingPanelIndex = 2;
                if (!this.leftPanels.includes(accountingPanelIndex)) {
                    this.leftPanels.push(accountingPanelIndex);
                }
                this.$nextTick(() => {
                    // The panel renders its content lazily, so the child
                    // component may not exist yet on the first click. Retry
                    // until it mounts, then delegate to it to focus the field.
                    if (this.$refs.accountingInformation) {
                        this.$refs.accountingInformation.focusOracleSiteNumber();
                    } else if (attempt < 20) {
                        setTimeout(() => this.focusOracleSiteNumber(attempt + 1), 50);
                    }
                });
            },
            initClient() {
                if (this.$route.path === '/new-client') {
                    this.id = null;
                    this.clientInfo = {
                        logo: 'http://bcrc.io/logo.png',
                        active: false,
                    };
                } else if (this.$route.query.id) {
                    const parsedId = parseFloat(this.$route.query.id);
                    if (!isNaN(parsedId) && parsedId > 0) {
                        this.id = parsedId;
                        this.getClientInfo(this.id);
                    } else {
                        this.$store.commit('displaySnackbar', 'ERROR: Invalid client ID');
                        this.$router.push('/clients');
                    }
                } else {
                    this.$router.push('/clients');
                }
            }
        },
        mounted() {
            this.initClient();
        },
        computed: {
            isNew() {
                return !this.id || this.id === 0 || this.$route.path === '/new-client';
            },
            readPricingPermission() {
                return this.$store.getters['auth/hasPermission']('client_pricing_view');
            },
            editPermissionsPermission() {
                if (this.$store.getters['auth/isInternalUser']) {
                    return this.$store.getters['auth/hasPermission']('client_edit');
                } else {
                    return this.$store.getters['auth/hasPermission']('vendor_admin');
                }
            }
        },
        watch: {
            clientInfo: {
                handler: function () {

                },
                deep: true
            },
            '$route': function () {
                this.initClient();
            }
        }
    }
</script>


<style>

  .v-expansion-panel-content__wrap {
    padding: 0;
  }
</style>



// WEBPACK FOOTER //
// src/components/EditClient.vue